/**
 * Onam durumunun okunması — tek yer.
 *
 * NEDEN AYRI DOSYA
 *
 * Dosya ucu onamların TAMAMINI döndürüyor, geri çekilmişler dahil:
 * geçmiş de dosyanın parçası ve "ne zaman alındı, ne zaman geri
 * çekildi" sorusunun cevabı orada. Ama bu, okuyan tarafa bir
 * sorumluluk bırakıyor — geri çekilmiş satırı elemek — ve o
 * sorumluluk `Danisan.tsx` içinde tek bir `map` çağrısına
 * gömülmüştü:
 *
 *     const verilen = new Set(dosya.onamlar.map((o) => o.kapsam))
 *
 * Süzgeç yoktu. Sonuç, uygulayıcının görebileceği en kötü çelişkiydi:
 * ekranda "Alındı" yazıyor, "Ölçümü başlat" düğmesi kapalı duruyor ve
 * sebebi hiçbir yerde yazmıyor. Kapı doğru çalışıyordu (`onam-durumu`
 * ucu `revoked_at IS NULL` bakıyor); yalan söyleyen rozetti.
 *
 * KVKK tarafında bu, geri çekilmiş bir izni alınmış sanmak demek —
 * yani ailenin geri aldığı bir rızaya dayanarak işlem yapmak.
 *
 * Mantık buraya çıkarıldı ki sınanabilsin. `apps/web`in başka
 * sınaması yok; bu dosyanınki `test/onam.ts`.
 */

export interface OnamSatiri {
  id: string
  kapsam: string
  geriCekildi: string | null
}

/**
 * Şu an GEÇERLİ olan onam satırı.
 *
 * Aynı kapsam birden çok kez verilmiş olabilir (alındı, geri çekildi,
 * yeniden alındı). Geçerli olan, geri çekilmemiş olandır; birden
 * fazlaysa sonuncusu.
 */
export function aktifOnam<T extends OnamSatiri>(onamlar: T[], kapsam: string): T | undefined {
  return onamlar.filter((o) => o.kapsam === kapsam && !o.geriCekildi).slice(-1)[0]
}

/**
 * Geçerli onamı olmayan bir kapsam için EN SON geri çekilmiş satır.
 *
 * "Yok" demek yetmiyor: uygulayıcı onamı aldığını hatırlıyor ve
 * ekranın yanıldığını sanıyor. Geri çekildiğini ve ne zaman
 * çekildiğini görünce ne olduğunu anlıyor.
 */
export function geriCekilmisOnam<T extends OnamSatiri>(
  onamlar: T[],
  kapsam: string,
): T | undefined {
  if (aktifOnam(onamlar, kapsam)) return undefined
  return onamlar.filter((o) => o.kapsam === kapsam && o.geriCekildi).slice(-1)[0]
}

/** Şu an geçerli onamların kapsam kümesi */
export function gecerliKapsamlar(onamlar: OnamSatiri[]): Set<string> {
  return new Set(onamlar.filter((o) => !o.geriCekildi).map((o) => o.kapsam))
}

/**
 * Ölçüm başlatılabilir mi.
 *
 * Sunucudaki `onam-durumu` ucuyla AYNI kural: aydınlatma ve ölçüm
 * onamlarının ikisi de geçerli olacak. Arayüz kapıyı sunucudan
 * okuyor; bu işlev yalnızca sınamanın iki tarafın aynı şeyi
 * söylediğini doğrulayabilmesi için var.
 */
export function olcumBaslatilabilir(onamlar: OnamSatiri[]): boolean {
  const k = gecerliKapsamlar(onamlar)
  return k.has('data_processing') && k.has('assessment')
}
